import { ReadingEggsReportPath, FeatureNotificationId } from 'teacher-ui-client/services/feature-notification'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type SideNavService from 'teacher-ui-client/services/side-nav'
import type ImpressionService from 'teacher-ui-client/services/impression'
import { SideNavAccordionItem, SideNavAccordionList } from 'teacher-ui-client/components/side-nav/accordion'
import graphUnfilled from '@images/icons/graph-unfilled.svg'
import { posthogEvent } from 'teacher-ui-client/modifiers/posthog'
import { Lozenge } from 'teacher-ui-client/components/lozenge'
import { t } from 'ember-intl'
import { FeatureNotification } from 'teacher-ui-client/helpers/feature-notification'

export class ReadingEggsReportsAccordion extends Component<unknown> {
  @service impression!: ImpressionService

  @service sideNav!: SideNavService

  get hasNewReadingEggsReports() {
    return Object.values(ReadingEggsReportPath).some((reportPath) => {
      return !this.impression.wasMade(reportPath)
    })
  }

  get uiScopeModels() {
    return this.sideNav.uiScopeModels ?? []
  }

  <template>
    <SideNavAccordionList
      @iconUrl={{graphUnfilled}}
      @iconAlt="Sidenav bar"
      @label={{t "sideNav.reports.title"}}
      @showNotificationDot={{this.hasNewReadingEggsReports}}
    >
      <SideNavAccordionItem
        @route="reporting.ui-scope.re.dashboard"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="reading" page="dashboard"}}
      >{{t "sideNav.reports.dashboard"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.re.summary.table"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="reading" page="summary"}}
      >{{t "sideNav.reports.summary"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.re.time-on-task"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="reading" page="time-on-task"}}
      >{{t "sideNav.reports.timeOnTask"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        data-test-re-standards-performance
        @route="reporting.ui-scope.re.standards-performance"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="reading" page="standards-performance"}}
      >{{t "sideNav.reports.standards"}}
        {{#if
          (FeatureNotification
            FeatureNotificationId.NewReportForPath reportPath=ReadingEggsReportPath.StandardsPerformance
          )
        }}
          <Lozenge data-test-reading-new-lozenge>{{t "new"}}</Lozenge>
        {{/if}}
      </SideNavAccordionItem>
      <SideNavAccordionItem
        data-test-re-lesson-performance
        @route="reporting.ui-scope.re.lesson-performance"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="reading" page="lesson-performance"}}
      >{{t "sideNav.reports.lessonPerformance"}}
        {{#if
          (FeatureNotification
            FeatureNotificationId.NewReportForPath reportPath=ReadingEggsReportPath.LessonPerformance
          )
        }}
          <Lozenge data-test-reading-new-lozenge>{{t "new"}}</Lozenge>
        {{/if}}
      </SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.re.course-progress.reading"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="reading" page="course-progress"}}
      >{{t "sideNav.reports.reading"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.re.assessment-scores"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="reading" page="assessment-scores"}}
      >{{t "sideNav.reports.mapQuizzes"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.re.driving-tests"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="reading" page="quizzes-completed"}}
      >{{t "sideNav.reports.drivingTests"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.re.lexile-details"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="reading" page="lexile-details"}}
      >{{t "sideNav.reports.lexileGrowth"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.re.course-progress.spelling"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="reading" page="progress-spelling"}}
      >{{t "sideNav.reports.spelling"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.re.stories-written"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="reading" page="stories-written"}}
      >{{t "sideNav.reports.storiesWritten"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.re.storylands"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="reading" page="storylands"}}
      >{{t "sideNav.reports.storylands"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.re.books-read"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="reading" page="books-read"}}
      >{{t "sideNav.reports.booksRead"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.re.skills-mastered.cohort.students-results"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="reading" page="skills-mastered-cohort-students-results"}}
      >{{t "sideNav.reports.skillsMastered"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.re.awards-earned"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="reading" page="awards-earned"}}
      >{{t "sideNav.reports.certificatesEarned"}}</SideNavAccordionItem>

    </SideNavAccordionList>
  </template>
}

export default ReadingEggsReportsAccordion
